import { Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLabel } from '@angular/material/form-field';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { NgMapsCoreModule } from '@ng-maps/core';
import { NgMapsGoogleModule } from '@ng-maps/google';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DerTableComponent, MultiStatChartComponent } from '@components';
import { SiteDto, Timestamp } from '@dto';
import { getSiteColor } from '@helpers';
import { SiteStatsService } from '../../../../services/site-stats.service';

@Component({
  imports: [
    TranslatePipe,
    DerTableComponent,
    NgMapsCoreModule,
    NgMapsGoogleModule,
    MatButtonModule,
    MatDivider,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatSlideToggle,
    MatFormField,
    MatExpansionModule,
    MultiStatChartComponent,
    MatLabel,
  ],
  selector: 'monitor-ui-site-diagnostics',
  styleUrls: ['./site-diagnostics.component.scss'],
  templateUrl: './site-diagnostics.component.html',
})
export class SiteDiagnosticsComponent implements OnInit {
  aDate = new Date();
  adjustedPower: Timestamp[] = [];
  bDate = new Date();
  committedDemand: Timestamp[] = [];
  dateCurrentInterval = 30;
  dateCurrentValue = 'Current';
  dates = ['Current'];
  demandBuilding: Timestamp[] = [];
  demandSiteMeter: Timestamp[] = [];
  futureDemandBuilding: Timestamp[] = [];
  intervals = [1, 5, 30];
  maxDate = this.aDate.setDate(this.aDate.getDate() + 1);
  minDate = this.bDate.setDate(this.bDate.getDate() - 1);
  nominalPower: Timestamp[] = [];
  overviewData: {
    title: string;
    stats: Timestamp[][];
    labels: string[];
    colors: string[];
    units: string;
  }[];
  reactivePower: Timestamp[] = [];
  @Input() site: SiteDto;
  siteAmperage: Timestamp[] = [];
  sitePowerFactor: Timestamp[] = [];
  siteVoltage: Timestamp[] = [];
  totalPower: Timestamp[] = [];
  upperBound: Timestamp[] = [];
  protected readonly getSiteColor = getSiteColor;

  constructor(
    private siteStatService: SiteStatsService,
    private route: ActivatedRoute,
  ) {}

  changedInterval(): void {
    this.loadStats();
  }

  ngOnInit(): void {
    this.loadStats();
  }

  private loadStats(): void {
    const siteId = this.route.snapshot.paramMap.get('siteId');
    forkJoin([
      this.siteStatService.siteStat$(siteId, 'BUILDING', this.dateCurrentInterval, this.minDate, this.maxDate, true),
      this.siteStatService.siteStat$(siteId, 'SITE_METER', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(siteId, 'SITE_VOLTAGE', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(
        siteId,
        'OE_PREDICTED_BUILDING',
        this.dateCurrentInterval,
        this.minDate,
        this.maxDate,
      ),
      this.siteStatService.siteStat$(siteId, 'SITE_AMPERAGE', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(siteId, 'SITE_POWER_FACTOR', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(
        siteId,
        'OE_COMMITTED_DEMAND',
        this.dateCurrentInterval,
        this.minDate,
        this.maxDate,
      ),
      this.siteStatService.siteStat$(siteId, 'OE_UPPER_BOUND', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(
        siteId,
        'SITE_REACTIVE_POWER',
        this.dateCurrentInterval,
        this.minDate,
        this.maxDate,
      ),
      this.siteStatService.siteStat$(
        siteId,
        'SITE_ADJUSTED_POWER',
        this.dateCurrentInterval,
        this.minDate,
        this.maxDate,
      ),
      this.siteStatService.siteStat$(siteId, 'SITE_TOTAL_POWER', this.dateCurrentInterval, this.minDate, this.maxDate),
      this.siteStatService.siteStat$(siteId, 'OE_NOMINAL_POWER', this.dateCurrentInterval, this.minDate, this.maxDate),
    ])
      .pipe(
        tap(stat => {
          this.demandBuilding = stat[0];
          this.siteVoltage = stat[2];
          this.futureDemandBuilding = stat[3];
          this.demandSiteMeter = stat[1];
          this.siteAmperage = stat[4];
          this.sitePowerFactor = stat[5];
          this.committedDemand = stat[6];
          this.upperBound = stat[7];
          this.reactivePower = stat[8];
          this.adjustedPower = stat[9];
          this.totalPower = stat[10];
          this.nominalPower = stat[11];
        }),
      )
      .subscribe(() => {
        this.overviewData = [
          /**
           * Same labels are gouped under same checkbox in the legend.
           * Future serie has to be passed in the array after the real one,
           * cause the second one with the same label will be dashed.
           * Colors obviously should reflect the order of labels and series.
           */
          {
            colors: ['#008285', '#C2185B', '#008285', '#043B9F', '#F06705'],
            labels: ['Building Demand', 'Site Meter', 'Building Demand', 'Committed Demand', 'Upper Bound'],
            stats: [
              this.demandBuilding,
              this.demandSiteMeter,
              this.futureDemandBuilding,
              this.committedDemand,
              this.upperBound,
            ],
            title: 'Demand',
            units: 'kW',
          },
          // {
          //   colors: ['#008285'],
          //   labels: ['Site S1'],
          //   stats: [[]],
          //   title: 'Energy',
          //   units: '',
          // },
          {
            colors: ['#008285', '#C2185B', '#043B9F', '#F06705'],
            labels: ['Reactive Power', 'Adjusted Power', 'Total Power', 'Nominal Power'],
            stats: [this.reactivePower, this.adjustedPower, this.totalPower, this.nominalPower],
            title: 'Power',
            units: 'kW',
          },
          {
            colors: ['#008285'],
            labels: ['Amperage'],
            stats: [this.siteAmperage],
            title: 'Amps',
            units: 'A',
          },
          {
            colors: ['#008285'],
            labels: ['Voltage'],
            stats: [this.siteVoltage],
            title: 'Voltage',
            units: 'V',
          },
          {
            colors: ['#008285'],
            labels: ['Power Factor'],
            stats: [this.sitePowerFactor],
            title: 'PF',
            units: '',
          },
        ];
      });
  }
}
